/**
 * API Configuration
 * Central location for API URLs and endpoint constants
 */

/**
 * Get the base API URL from environment variables
 * Falls back to dev API Gateway if not set
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ||
  'https://dev.api.ciphergrid.app'

export const ADMIN_API_KEY = import.meta.env.VITE_ADMIN_API_KEY || ''

/**
 * API endpoint paths
 * These are proxied through the API Gateway to Cloud Functions
 *
 * Format: /function-group/function-name
 * Example: /admin/get-features → routed to dev-admin-get-features
 */
export const API_ENDPOINTS = {
  // Admin endpoints
  ADMIN: {
    GET_FEATURES: '/admin/get-features',
    UPDATE_AI_PROVIDER: '/admin/update-ai-provider',
    UPDATE_LAYOUT_STRATEGY: '/admin/update-layout-strategy',
    UPDATE_FEATURE_FLAG: '/admin/update-feature-flag',
    BAN_USER: '/admin/ban-user',
    UNBAN_USER: '/admin/unban-user',
    GET_BAN_STATUS: '/admin/get-ban-status',
    GET_STORE_SECTIONS: '/admin/get-store-sections',
    CREATE_STORE_SECTION: '/admin/create-store-section',
    UPDATE_STORE_SECTION: '/admin/update-store-section',
    DELETE_STORE_SECTION: '/admin/delete-store-section',
    CREATE_PRODUCT: '/admin/create-product',
    GET_PRODUCTS: '/admin/get-products',
    UPDATE_PRODUCT_METADATA: '/admin/update-product-metadata',
    GENERATE_COVER_IMAGE: '/admin/generate-cover-image',
    UPLOAD_PRODUCT_IMAGE: '/admin/upload-product-image',
    GET_PUZZLES: '/admin/get-puzzles',
    GET_PUZZLE: '/admin/get-puzzle',
    GET_PRICE_POINTS: '/admin/get-price-points',
    CREATE_PRICE_POINT: '/admin/create-price-point',
    UPDATE_PRICE_POINT: '/admin/update-price-point',
    DELETE_PRICE_POINT: '/admin/delete-price-point',
    BULK_UPDATE_PRICING: '/admin/bulk-update-pricing',
    API_KEYS: '/admin/api-keys',
    // Seed endpoints (bulk import from config files)
    SEED_FEATURES: '/admin/seed-features',
    SEED_CATEGORIES: '/admin/seed-categories',
    SEED_DIFFICULTIES: '/admin/seed-difficulties',
    SEED_MAINTENANCE: '/admin/seed-maintenance',
    SEED_PRICE_POINTS: '/admin/seed-price-points',
    // Export endpoints
    EXPORT_WORDS: '/admin/export-words',
    IMPORT_WORDS: '/admin/import-words',
    // Word management dashboard
    WORD_MANAGEMENT_OVERVIEW: '/admin/word-management/overview',
    WORD_MANAGEMENT_VALIDATION_STATUS: '/admin/word-management/validation-status',
    WORD_MANAGEMENT_QUALITY_METRICS: '/admin/word-management/quality-metrics',
    WORD_MANAGEMENT_SEARCH_WORDS: '/admin/word-management/search-words',
    // AI Benchmark endpoints
    BENCHMARK_PROVIDERS: '/admin/benchmark-run',
    BENCHMARK_RUN: '/admin/benchmark-run',
  },

  // Puzzle endpoints (public puzzle configuration)
  PUZZLE: {
    GET_CATEGORIES: '/puzzle/get-categories',
    GET_DIFFICULTIES: '/puzzle/get-difficulties',
  },

  // Voting endpoints (read-only endpoint remains public, admin operation under /admin/)
  VOTING: {
    GET_RESULTS: '/voting/get-voting-results',
    CLOSE_VOTING: '/admin/close-voting',
  },

  // Maintenance endpoints (generic path for any maintenance schedule)
  MAINTENANCE: {
    RUN_WEEKLY: '/admin/maintenance',
    GET_CONFIG: '/admin/get-maintenance-config',
    GET_HISTORY: '/admin/get-maintenance-history',
  },

  // Offline wordlist endpoints
  WORDLISTS: {
    REBUILD: '/admin/wordlists-rebuild',
    STATUS: '/admin/wordlists-status',
  },

  // Version and bootstrap
  VERSION: '/version',
  BOOTSTRAP: '/api-bootstrap',
} as const

/**
 * Request timeout tiers in milliseconds
 * Different operations have different timeout requirements
 */
export const API_TIMEOUTS = {
  /** Standard operations (most endpoints): 30 seconds */
  STANDARD: 30000,
  /** Medium operations (exports, imports, bulk updates): 2 minutes */
  MEDIUM: 120000,
  /** Long operations (maintenance, wordlist rebuild): 10 minutes */
  LONG: 600000,
} as const

/**
 * Default API timeout (standard operations)
 */
export const API_TIMEOUT = API_TIMEOUTS.STANDARD

/**
 * Default headers for API requests
 */
export const DEFAULT_HEADERS: Record<string, string> = {
  'Content-Type': 'application/json',
}

if (ADMIN_API_KEY) {
  DEFAULT_HEADERS['X-API-Key'] = ADMIN_API_KEY
}

/**
 * Token validation endpoint
 * Used by reauth flow to test if a token is valid
 *
 * Requirements:
 * - Must require admin authentication (401 on invalid token)
 * - Should be lightweight (fast response)
 * - Should be an endpoint the admin site commonly uses
 *
 * Current: Uses GET_FEATURES (small JSON, ~50ms response)
 */
export const TOKEN_VALIDATION_ENDPOINT = API_ENDPOINTS.ADMIN.GET_FEATURES
